import style from './style.module.scss'
import {Button, Filter, OptionButton, PageTitle, Table} from "@/theme";
import cn from "classnames/bind";
import {useState} from "react";
import {useApi, useApiMutation, useTempData} from "@/lib/hook";
import {Spin, Tag} from "antd";
import {DeleteOutlined, EyeOutlined} from "@ant-design/icons";
import {dateFormat, paginationHelper} from "@/lib/utils";
import {UserEditModal} from "@/components/user/modal/userEditModal";
import {UserDetailsModal} from "@/components/user/modal/userDetailsModal";

const initFilterData = {
    page: 1,
}

export const AllUsers = () => {
    const {data: tempData, setTempData} = useTempData('users_page', initFilterData);
    const {data, isFetching, refetch} = useApi({
        route:{name: 'user.all'},
        data:{
            page: tempData.page,
            filter: tempData.filter
        }
    })
    const userDelete = useApiMutation({
        onSuccess: () =>{
            refetch()
        }
    })
    const [modalEditSetting, setModalEditSetting] = useState({
        open: false,
        userId: null
    })
    const [modalDetailsSetting, setModalDetailsSetting] = useState({
        open: false,
        userId: null
    })

    const columns = [
        {title: 'User id', dataIndex: 'id', width: '80px',
            render: (id) => `#${id}`
        },
        {title: 'User name', width: '180px',
            render: (user)=>(
                <>
                    <div className={style.title}>
                        {user.first_name} {user.last_name}
                    </div>
                    <div className={style.sub_title}>
                        {user.email}
                    </div>
                </>
            )
        },
        {title: 'Subscription type', dataIndex: 'subscription', width: '150px',
            render: (subscription)=>(
                <>
                    <div className={style.title}>
                        {subscription.plan}
                    </div>
                    <div className={style.sub_title}>
                        {subscription.billing_interval} / {subscription.billing_period}
                    </div>
                </>
            )
        },
        {title: 'Date of start', dataIndex: ['subscription', 'schedule_start'], width: '120px',
            render: (date)=>dateFormat(date)
        },
        {title: 'Date of end', dataIndex: ['subscription', 'schedule_next_payment'], width: '120px',
            render: (date)=>dateFormat(date)
        },
        {title: 'Status', dataIndex: ['subscription', 'is_active'], width: '80px',
            render: (status)=>(
                status
                    ? (
                        <Tag
                            bordered={false}
                            color="green"
                        >
                            Active
                        </Tag>
                    )
                    : (
                        <Tag
                            bordered={false}
                            color="error"
                        >
                            Expired
                        </Tag>
                    )
            )
        },

        {title: 'Actions', width: '85px', className: 'tl_center',
            render: (item) => (
                <div
                    className={style.actions_cell}
                >
                    <Button
                        icon={<EyeOutlined />}
                        type={'text'}
                        onClick={()=>{
                            showUserDetails(item.id)
                        }}
                    />
                    <OptionButton>
                        <OptionButton.Button
                            onClick={()=>{
                                showUserEdit(item.id)
                            }}
                        >
                            Edit
                        </OptionButton.Button>
                        <OptionButton.Button
                            onClick={()=>{
                                onDeleteUser(item.id)
                            }}
                            confirm={{
                                title: `Are you sure you want to delete User #${item.id} ?`,
                                content: 'If you delete a user, all data about him will be irretrievably lost',
                                type: 'delete',
                                okButtonProps: {
                                    type: 'primary',
                                    icon: <DeleteOutlined />,
                                }
                            }}
                            type={'danger'}
                        >
                            Delete
                        </OptionButton.Button>
                    </OptionButton>
                </div>
            )
        },

    ]

    const onChangePage = (page) => {
        setTempData({
            page,
            filter: {
                ...tempData.filter
            }
        })
    }
    const onChangeFilter = (value) => {
        setTempData({
            page: 1,
            filter: {
                ...tempData.filter,
                ...value,
            }
        })
    }

    const showUserDetails = (userId) =>{
        setModalDetailsSetting((prev)=>({
            open: !prev.open,
            userId,
        }))
    }

    const closeUserModalDetails = () => {
        setModalDetailsSetting((prev)=>({
            open: !prev.open,
            userId: null,
        }))
    }

    const showUserEdit = (userId) => {
        setModalEditSetting((prev)=>({
            open: !prev.open,
            userId,
        }))
    }

    const closeUserModalEdit = () => {
        setModalEditSetting((prev)=>({
            open: !prev.open,
            userId: null,
        }))
    }

    const onDeleteUser = (id) =>{
       userDelete.mutate({
           route:{name: 'user.delete', query:{id}}
       })
    }

    return(
        <>
            <PageTitle
                title={'All users'}
            />

            <Spin spinning={isFetching}>
                <UserFilter
                    initialValues={tempData}
                    onChangeFilter={onChangeFilter}
                />

                <Table
                    className={style.table}
                    style={{
                        marginTop: 16,
                    }}
                    dataSource={data?.data}
                    columns={columns}
                    rowKey={'id'}
                    pagination={data?.meta ? paginationHelper(data.meta, onChangePage) : false}
                />
            </Spin>

            <UserEditModal
                userId={modalEditSetting.userId}
                isModalOpen={modalEditSetting.open}
                setIsModalOpen={closeUserModalEdit}
                refetch={refetch}
            />

            <UserDetailsModal
                userId={modalDetailsSetting.userId}
                isModalOpen={modalDetailsSetting.open}
                setIsModalOpen={closeUserModalDetails}
            />
        </>
    )
}

const UserFilter = ({initialValues, onChangeFilter}) =>{
    return(
        <Filter
            initialValues={initialValues}
            onChange={onChangeFilter}
            useFilters={[
                {
                    type: 'search',
                    name: 'q',
                    placeholder: 'Search by id or email',
                    width: 368,
                },
                [
                    {
                        type: 'select',
                        name: 'subscription_type',
                        placeholder: 'Subscription type',
                        options: [
                            {value: 'premium', label: 'PREMIUM'},
                            {value: 'pro', label: 'PRO'},
                            {value: 'essentials', label: 'ESSENTIALS'},
                        ]
                    },
                    {
                        type: 'select',
                        name: 'status',
                        placeholder: 'Status',
                        options: [
                            {value: '1', label: 'Active'},
                            {value: '0', label: 'Expired'},
                        ]
                    },
                ],
            ]}
            wrapper={(children, level)=>(
                <div className={cn(style.filter, {[style.main_level]: level === 1})}>
                    {children}
                </div>
            )}
        />
    )
}